import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Building2, HardHat, Laptop, MapPinned, Plus, Warehouse } from "lucide-react";
import { Button, ErrorBanner, Field, Input, LoadingState, PageHeader, Panel, Select, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { requireSupabase } from "../lib/supabase";
import type { DepartmentRow, StorageLocationRow, TechnicianRow, WarehouseDeviceRow, WarehouseRow } from "../types";
import { useDeveloperSettings } from "../lib/developerSettings";

type Section = "warehouses" | "locations" | "departments" | "technicians" | "devices";
type MasterData = {
  warehouses: WarehouseRow[];
  locations: StorageLocationRow[];
  departments: DepartmentRow[];
  technicians: TechnicianRow[];
  devices: WarehouseDeviceRow[];
};

const warehouseIds = {
  OB: "00000000-0000-4000-8000-000000000001",
  MZ: "00000000-0000-4000-8000-000000000002"
} as const;

const demoWarehouses: WarehouseRow[] = [
  { id: warehouseIds.OB, code: "OB", name: "Oberhausen", is_active: true },
  { id: warehouseIds.MZ, code: "MZ", name: "Merzenich", is_active: true }
];
const demoLocations: StorageLocationRow[] = demoWarehouses.flatMap((warehouse) =>
  ["A", "B", "C"].flatMap((row) => [1, 2, 3, 4, 5].map((number) => ({
    id: `demo-${warehouse.code}-${row}${number}`,
    warehouse_id: warehouse.id,
    code: `${row}${number}`,
    description: null,
    is_active: true
  })))
);
const demoData: MasterData = {
  warehouses: demoWarehouses,
  locations: demoLocations,
  departments: [
    { id: "demo-dep-1", code: "ELEKTRO", name: "Elektro", is_active: true },
    { id: "demo-dep-2", code: "METALLBAU", name: "Metallbau", is_active: true },
    { id: "demo-dep-3", code: "SANITAER", name: "Sanitär", is_active: true },
    { id: "demo-dep-4", code: "SONNENSCHUTZ", name: "Sonnenschutz", is_active: true }
  ],
  technicians: [
    { id: "demo-technician-1", full_name: "Max Mustermann", department_id: "demo-dep-1", is_active: true },
    { id: "demo-technician-2", full_name: "Erika Beispiel", department_id: "demo-dep-2", is_active: true }
  ],
  devices: [
    { id: "demo-device-1", warehouse_id: warehouseIds.OB, name: "Samsung Lager OB", device_type: "android", printer_model: "Zebra ZD421d", is_active: true },
    { id: "demo-device-2", warehouse_id: warehouseIds.OB, name: "Windows Lager OB", device_type: "windows", printer_model: "Zebra ZD421d", is_active: true },
    { id: "demo-device-3", warehouse_id: warehouseIds.MZ, name: "Samsung Lager MZ", device_type: "android", printer_model: "Zebra ZD421d", is_active: true },
    { id: "demo-device-4", warehouse_id: warehouseIds.MZ, name: "Windows Lager MZ", device_type: "windows", printer_model: "Zebra ZD421d", is_active: true }
  ]
};

const sectionDefinitions = [
  { key: "warehouses" as const, title: "Lager", description: "Feste Standorte", icon: Warehouse },
  { key: "locations" as const, title: "Lagerplätze", description: "Regale und Fächer", icon: MapPinned },
  { key: "departments" as const, title: "Abteilungen", description: "Fachbereiche", icon: Building2 },
  { key: "technicians" as const, title: "Monteure", description: "Abholerauswahl", icon: HardHat },
  { key: "devices" as const, title: "Geräte", description: "Handys, PCs und Drucker", icon: Laptop }
];

export function MasterDataPage() {
  const { cloudConfigured, profile } = useAuth();
  const { exampleTexts } = useDeveloperSettings();
  const [data, setData] = useState<MasterData>(demoData);
  const [section, setSection] = useState<Section>("locations");
  const [loading, setLoading] = useState(cloudConfigured);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [locationDraft, setLocationDraft] = useState<{ warehouse_id: string; code: string; description: string }>({ warehouse_id: warehouseIds.OB, code: "", description: "" });
  const [departmentDraft, setDepartmentDraft] = useState({ code: "", name: "" });
  const [technicianDraft, setTechnicianDraft] = useState({ full_name: "", department_id: "" });
  const [deviceDraft, setDeviceDraft] = useState<{ warehouse_id: string; name: string; device_type: "android" | "windows"; printer_model: string }>({ warehouse_id: warehouseIds.OB, name: "", device_type: "android", printer_model: "" });
  const canManage = cloudConfigured && (profile?.role === "admin" || profile?.role === "dispatcher");

  const load = useCallback(async () => {
    if (!cloudConfigured) return;
    setLoading(true);
    const client = requireSupabase();
    const [warehouses, locations, departments, technicians, devices] = await Promise.all([
      client.from("warehouses").select("id,code,name,is_active").order("name"),
      client.from("storage_locations").select("id,warehouse_id,code,description,is_active").order("code"),
      client.from("departments").select("id,code,name,is_active").order("name"),
      client.from("technicians").select("id,full_name,department_id,is_active").order("full_name"),
      client.from("warehouse_devices").select("id,warehouse_id,name,device_type,printer_model,is_active").order("name")
    ]);
    const firstError = warehouses.error || locations.error || departments.error || technicians.error || devices.error;
    if (firstError) setError(`Stammdaten konnten nicht geladen werden: ${firstError.message}`);
    else {
      setData({
        warehouses: (warehouses.data ?? []) as WarehouseRow[],
        locations: (locations.data ?? []) as StorageLocationRow[],
        departments: (departments.data ?? []) as DepartmentRow[],
        technicians: (technicians.data ?? []) as TechnicianRow[],
        devices: (devices.data ?? []) as WarehouseDeviceRow[]
      });
      setError("");
    }
    setLoading(false);
  }, [cloudConfigured]);

  useEffect(() => { load(); }, [load]);

  const warehouseById = useMemo(() => new Map(data.warehouses.map((item) => [item.id, item])), [data.warehouses]);
  const departmentById = useMemo(() => new Map(data.departments.map((item) => [item.id, item])), [data.departments]);
  const countFor = (key: Section) => data[key].filter((item) => item.is_active).length;

  async function mutate(action: () => PromiseLike<{ error: { message: string } | null }>, after: () => void) {
    setBusy(true);
    setError("");
    const result = await action();
    if (result.error) setError(result.error.message);
    else { after(); await load(); }
    setBusy(false);
  }

  async function addLocation(event: FormEvent) {
    event.preventDefault();
    const code = locationDraft.code.trim().toUpperCase();
    if (!/^[A-Z]+[0-9]+$/.test(code)) return setError("Der Lagerplatz muss beispielsweise A1 oder C5 heißen.");
    await mutate(
      () => requireSupabase().from("storage_locations").insert({ ...locationDraft, code, description: locationDraft.description.trim() || null }),
      () => setLocationDraft({ ...locationDraft, code: "", description: "" })
    );
  }

  async function addDepartment(event: FormEvent) {
    event.preventDefault();
    const code = departmentDraft.code.trim().toUpperCase().replace(/[^A-Z0-9_]/g, "_");
    if (!code || !departmentDraft.name.trim()) return setError("Code und Name der Abteilung sind erforderlich.");
    await mutate(
      () => requireSupabase().from("departments").insert({ code, name: departmentDraft.name.trim() }),
      () => setDepartmentDraft({ code: "", name: "" })
    );
  }

  async function addTechnician(event: FormEvent) {
    event.preventDefault();
    if (!technicianDraft.full_name.trim() || !technicianDraft.department_id) {
      return setError("Name und Abteilung des Monteurs sind erforderlich.");
    }
    await mutate(
      () => requireSupabase().from("technicians").insert({ full_name: technicianDraft.full_name.trim(), department_id: technicianDraft.department_id }),
      () => setTechnicianDraft({ full_name: "", department_id: "" })
    );
  }

  async function updateTechnicianDepartment(id: string, departmentId: string) {
    await mutate(
      () => requireSupabase().from("technicians").update({ department_id: departmentId || null }).eq("id", id),
      () => undefined
    );
  }

  async function addDevice(event: FormEvent) {
    event.preventDefault();
    if (!deviceDraft.name.trim()) return;
    await mutate(
      () => requireSupabase().from("warehouse_devices").insert({ ...deviceDraft, name: deviceDraft.name.trim(), printer_model: deviceDraft.printer_model.trim() || null }),
      () => setDeviceDraft({ ...deviceDraft, name: "", printer_model: "" })
    );
  }

  async function toggleActive(table: "storage_locations" | "departments" | "technicians" | "warehouse_devices", id: string, active: boolean) {
    await mutate(() => requireSupabase().from(table).update({ is_active: !active }).eq("id", id), () => undefined);
  }

  function activeControl(table: Parameters<typeof toggleActive>[0], id: string, active: boolean) {
    return canManage
      ? <Button variant="secondary" className="button--small" disabled={busy} onClick={() => toggleActive(table, id, active)}>{active ? "Deaktivieren" : "Aktivieren"}</Button>
      : <StatusBadge tone={active ? "success" : "neutral"}>{active ? "Aktiv" : "Inaktiv"}</StatusBadge>;
  }

  function renderSection() {
    if (section === "warehouses") return (
      <Panel title="Lagerstandorte" description="Die Standortcodes sind fest und werden nicht im Tagesbetrieb geändert.">
        <div className="table-shell table-shell--embedded"><table><thead><tr><th>Code</th><th>Lager</th><th>Status</th></tr></thead><tbody>{data.warehouses.map((item) => <tr key={item.id}><td><strong>{item.code}</strong></td><td>{item.name}</td><td><StatusBadge tone={item.is_active ? "success" : "neutral"}>{item.is_active ? "Aktiv" : "Inaktiv"}</StatusBadge></td></tr>)}</tbody></table></div>
      </Panel>
    );

    if (section === "locations") return (
      <Panel title="Lagerplätze" description={`${data.locations.filter((item) => item.is_active).length} aktive Plätze in den freigegebenen Lagern`}>
        {canManage && <form className="inline-form" onSubmit={addLocation}><Field label="Lager"><Select value={locationDraft.warehouse_id} onChange={(event) => setLocationDraft({ ...locationDraft, warehouse_id: event.target.value })}>{data.warehouses.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field><Field label="Regalplatz"><Input value={locationDraft.code} onChange={(event) => setLocationDraft({ ...locationDraft, code: event.target.value })} placeholder={exampleTexts.storageLocation} required /></Field><Field label="Beschreibung"><Input value={locationDraft.description} onChange={(event) => setLocationDraft({ ...locationDraft, description: event.target.value })} placeholder={exampleTexts.optional} /></Field><Button type="submit" disabled={busy}><Plus size={16} />Hinzufügen</Button></form>}
        <div className="table-shell table-shell--embedded"><table><thead><tr><th>Lager</th><th>Platz</th><th>Beschreibung</th><th>Status</th></tr></thead><tbody>{data.locations.map((item) => <tr key={item.id}><td>{warehouseById.get(item.warehouse_id)?.name ?? "–"}</td><td><strong>{item.code}</strong></td><td>{item.description || "–"}</td><td>{activeControl("storage_locations", item.id, item.is_active)}</td></tr>)}</tbody></table></div>
      </Panel>
    );

    if (section === "departments") return (
      <Panel title="Abteilungen" description="Fachliche Zuordnung der Aufträge">
        {canManage && <form className="inline-form" onSubmit={addDepartment}><Field label="Code"><Input value={departmentDraft.code} onChange={(event) => setDepartmentDraft({ ...departmentDraft, code: event.target.value })} placeholder={exampleTexts.departmentCode} required /></Field><Field label="Name"><Input value={departmentDraft.name} onChange={(event) => setDepartmentDraft({ ...departmentDraft, name: event.target.value })} placeholder={exampleTexts.departmentName} required /></Field><Button type="submit" disabled={busy}><Plus size={16} />Hinzufügen</Button></form>}
        <div className="table-shell table-shell--embedded"><table><thead><tr><th>Code</th><th>Name</th><th>Status</th></tr></thead><tbody>{data.departments.map((item) => <tr key={item.id}><td><strong>{item.code}</strong></td><td>{item.name}</td><td>{activeControl("departments", item.id, item.is_active)}</td></tr>)}</tbody></table></div>
      </Panel>
    );

    if (section === "technicians") return (
      <Panel title="Monteure" description="Auswahlliste für die Abholung ohne eigenes Monteurkonto.">
        {canManage && <form className="inline-form" onSubmit={addTechnician}><Field label="Name"><Input value={technicianDraft.full_name} onChange={(event) => setTechnicianDraft({ ...technicianDraft, full_name: event.target.value })} required /></Field><Field label="Abteilung"><Select value={technicianDraft.department_id} onChange={(event) => setTechnicianDraft({ ...technicianDraft, department_id: event.target.value })} required><option value="">Abteilung auswählen</option>{data.departments.filter((item) => item.is_active).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field><Button type="submit" disabled={busy}><Plus size={16} />Hinzufügen</Button></form>}
        <div className="table-shell table-shell--embedded"><table><thead><tr><th>Name</th><th>Abteilung</th><th>Status</th></tr></thead><tbody>{data.technicians.map((item) => <tr key={item.id}><td><strong>{item.full_name}</strong></td><td>{canManage ? <Select value={item.department_id ?? ""} disabled={busy} onChange={(event) => updateTechnicianDepartment(item.id, event.target.value)}><option value="">Nicht zugeordnet</option>{data.departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}</Select> : (item.department_id ? departmentById.get(item.department_id)?.name ?? "Unbekannt" : "Nicht zugeordnet")}</td><td>{activeControl("technicians", item.id, item.is_active)}</td></tr>)}</tbody></table></div>
      </Panel>
    );

    return (
      <Panel title="Standortgeräte" description="Die konkrete Druckerverbindung folgt im Druckpaket; Modell und Lager können bereits gepflegt werden.">
        {canManage && <form className="inline-form" onSubmit={addDevice}><Field label="Lager"><Select value={deviceDraft.warehouse_id} onChange={(event) => setDeviceDraft({ ...deviceDraft, warehouse_id: event.target.value })}>{data.warehouses.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field><Field label="Gerätename"><Input value={deviceDraft.name} onChange={(event) => setDeviceDraft({ ...deviceDraft, name: event.target.value })} placeholder={exampleTexts.deviceName} required /></Field><Field label="Typ"><Select value={deviceDraft.device_type} onChange={(event) => setDeviceDraft({ ...deviceDraft, device_type: event.target.value as "android" | "windows" })}><option value="android">Android</option><option value="windows">Windows</option></Select></Field><Field label="Druckermodell"><Input value={deviceDraft.printer_model} onChange={(event) => setDeviceDraft({ ...deviceDraft, printer_model: event.target.value })} placeholder={exampleTexts.printerModel} /></Field><Button type="submit" disabled={busy}><Plus size={16} />Hinzufügen</Button></form>}
        <div className="table-shell table-shell--embedded"><table><thead><tr><th>Lager</th><th>Gerät</th><th>Typ</th><th>Drucker</th><th>Status</th></tr></thead><tbody>{data.devices.map((item) => <tr key={item.id}><td>{warehouseById.get(item.warehouse_id)?.name ?? "–"}</td><td><strong>{item.name}</strong></td><td>{item.device_type === "android" ? "Android" : "Windows"}</td><td>{item.printer_model || "Noch offen"}</td><td>{activeControl("warehouse_devices", item.id, item.is_active)}</td></tr>)}</tbody></table></div>
      </Panel>
    );
  }

  return (
    <>
      <PageHeader title="Stammdaten" description="Zentrale Vorgaben für Lager, Wareneingang und Abholung." action={<StatusBadge tone={cloudConfigured ? "success" : "warning"}>{cloudConfigured ? "Testcloud verbunden" : "Vorschaudaten"}</StatusBadge>} />
      {!cloudConfigured && <div className="notice-banner">Änderungen sind in der lokalen Vorschau deaktiviert. Nach Verbindung der Testcloud wird dieselbe Seite schreibbar.</div>}
      {cloudConfigured && !canManage && <div className="notice-banner">Lagerkonten sehen nur ihre freigegebenen Daten. Änderungen an Stammdaten übernimmt die Dispo oder ein Administrator.</div>}
      {error && <ErrorBanner message={error} />}
      <section className="master-grid master-grid--interactive">
        {sectionDefinitions.map((item) => <button type="button" className={`master-card ${section === item.key ? "master-card--active" : ""}`} key={item.key} onClick={() => setSection(item.key)}><span><item.icon size={22} /></span><div><strong>{item.title}</strong><p>{item.description}</p></div><b>{countFor(item.key)}</b></button>)}
      </section>
      {loading ? <LoadingState label="Stammdaten werden geladen …" /> : renderSection()}
    </>
  );
}
